import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MIN_COLUMN_BAR_HEIGHT } from '../../constants/widget-configuration';
import { handleMouseEnter, handleMouseLeave } from '../../helpers/tooltip-functions';
import { type ColumnKpiPercentageResponse, useChartKPIPercentageQuery } from '../../redux/config/chart-data';
import type { WidgetConfigurationData } from '../../redux/config/widgetApi';
import { getWidgets } from '../../redux/slices/layout-tabs-slice';
import OdinCustomDetailsWindow from '../CustomDetailsWindow/custom-details-window.component';
import { type DetailsWindowFieldsConfiguration } from '../CustomDetailsWindow/utils';
import CustomTooltip from '../CustomToolTip/CustomToolTip';
import ChartGenericWrapper from '../common/chart-generic-wrapper';
import { CustomXAxis } from '../common/custom-X-axis.component';
import { lineDotBaseProps } from '../common/line-dot-base-props';
import { compactNumberFormatter } from '../common/tick-formatter';
import type { ChartProps } from './chart.interface';
import { intervalCounter } from './generalFunctions/general-functions';
import { getHighestColumnChartValue } from './generalFunctions/get-domaincharts';
import { useXAxisTopSettings } from './hooks/use-x-axis-top-settings';
import useInvalidateChartResponse from './hooks/useInvalidateChartResponse';
import useStartEndDates from './hooks/useStartEndDates';

export default function OdinColumnKpiPercentage({ widgetId, layoutProps, activateAnimation }: ChartProps) {
  const [transformedData, setTransformedData] = useState<Array<ColumnKpiPercentageResponse>>();
  const [configurationData, setConfigurationData] = useState<WidgetConfigurationData>();
  const [detailsWindowVisible, setDetailsWindowVisible] = useState<boolean>(false);
  const [modalData, setModalData] = useState<DetailsWindowFieldsConfiguration>();

  const { startDate, endDate } = useStartEndDates();
  const xAxisSettings = useXAxisTopSettings();
  const widgets = useSelector(getWidgets);

  const {
    data: widgetChartData,
    isLoading: isWidgetChartDataLoading,
    isFetching: isWidgetChartDataFetching,
    isError,
    error,
  } = useChartKPIPercentageQuery(
    {
      url: configurationData?.dataURL,
      from: startDate,
      to: endDate,
    },
    {
      skip: !configurationData,
    }
  );

  const onShowModal = (data: DetailsWindowFieldsConfiguration) => {
    setModalData(data);
    setDetailsWindowVisible(true);
  };

  const onDialogHidden = () => setDetailsWindowVisible(false);
  const handleRefetch = useInvalidateChartResponse(widgetId);

  useEffect(() => {
    if (widgetChartData) {
      const transformedData = widgetChartData.map((data) => {
        const newData = { ...data, color: +data.value < +data.kpi ? '#C70039' : '#50C878' };

        if (newData.field === '') {
          newData.field = '[none]';
        }

        return newData;
      });

      setTransformedData(transformedData);
    }
  }, [widgetChartData]);

  useEffect(() => {
    if (widgets && widgets.length > 0) {
      setConfigurationData(widgets.find((widget) => +widget.widget.id === +widgetId)?.widget);
    }
  }, [widgets, widgetId]);

  return (
    <ChartGenericWrapper
      isError={isError}
      error={error}
      isFetching={isWidgetChartDataFetching}
      isLoading={isWidgetChartDataLoading}
      refetch={handleRefetch}
      widgetId={widgetId}
      widgetConfigurationData={configurationData && { ...configurationData, colors: [], layoutProps }}
      widgetChartData={widgetChartData}
      renderNoDataByDefault={!isWidgetChartDataFetching && !transformedData?.length}
    >
      {configurationData && transformedData && transformedData.length > 0 && (
        <>
          <OdinCustomDetailsWindow
            {...configurationData.widgetData}
            widgetId={widgetId}
            fieldValue={modalData?.firstField}
            visible={detailsWindowVisible}
            onDialogHidden={onDialogHidden}
          />
          <ResponsiveContainer height="99%">
            <ComposedChart
              data={transformedData}
              margin={{
                top: 20,
                right: 10,
                left: 10,
                bottom: 10,
              }}
            >
              <CartesianGrid vertical={false} />
              <Tooltip content={<CustomTooltip />} cursor={false} isAnimationActive={false} />
              <XAxis {...xAxisSettings} interval={intervalCounter(transformedData)} tick={CustomXAxis} />
              <YAxis
                type="number"
                label={{
                  value: 'Target KPI',
                  angle: -90,
                  position: 'insideLeft',
                  dy: 35,
                }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                domain={[0, getHighestColumnChartValue(transformedData, 'kpi')]}
                tickFormatter={compactNumberFormatter}
                interval={0}
                tick
              />
              <Bar
                name="Value"
                dataKey="value"
                onMouseEnter={() => handleMouseEnter('Value')}
                onMouseLeave={handleMouseLeave}
                onClick={(data) => onShowModal({ firstField: data.payload?.field })}
                minPointSize={MIN_COLUMN_BAR_HEIGHT}
                cursor="pointer"
                isAnimationActive={activateAnimation}
              >
                <LabelList dataKey="percentage" position="top" fontSize={12} fill="#000" />
                {transformedData.map((data, index) => (
                  <Cell key={`cell-${index}`} fill={data.color} />
                ))}
              </Bar>
              <Line
                type="linear"
                dataKey="kpi"
                stroke="#000"
                isAnimationActive={activateAnimation}
                dot={lineDotBaseProps}
              >
                <LabelList dataKey="kpi" position="top" offset={10} fontSize={12} fill="#000" />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        </>
      )}
    </ChartGenericWrapper>
  );
}
